import { Color } from "@repo/app-types/constants/colors";
import { Size } from "@repo/ui/helpers/helpers";
import { Pill, type PillColor } from "@repo/ui/Pills/pill";
import {
  IconArrowDownRight,
  IconArrowRight,
  IconArrowUpRight,
} from "@tabler/icons-react";
import type { ComponentType, FC } from "react";
import { twMerge } from "tailwind-merge";

export type DeltaTone = "positive" | "negative" | "neutral";

export const formatCount = (value: number | null | undefined) =>
  value === null || value === undefined ? "—" : value.toLocaleString("en-US");

export const formatPercent = (value: number | null | undefined, digits = 1) =>
  value === null || value === undefined
    ? "—"
    : `${(value * 100).toFixed(digits)}%`;

export const formatDeltaPercentage = (
  current: number | null | undefined,
  previous: number | null | undefined,
): { delta: string; tone: DeltaTone } => {
  if (current == null || previous == null) {
    return { delta: "—", tone: "neutral" };
  }
  if (previous === 0) {
    return current === 0
      ? { delta: "0%", tone: "neutral" }
      : { delta: "New", tone: "positive" };
  }
  const change = (current - previous) / previous;
  const delta = `${change >= 0 ? "+" : ""}${(change * 100).toFixed(0)}%`;
  const tone: DeltaTone =
    change > 0 ? "positive" : change < 0 ? "negative" : "neutral";
  return { delta, tone };
};

export const formatDeltaPoints = (
  current: number | null | undefined,
  previous: number | null | undefined,
): { delta: string; tone: DeltaTone } => {
  if (current == null || previous == null) {
    return { delta: "—", tone: "neutral" };
  }
  const change = current - previous;
  const delta = `${change >= 0 ? "+" : ""}${change} pts`;
  const tone: DeltaTone =
    change > 0 ? "positive" : change < 0 ? "negative" : "neutral";
  return { delta, tone };
};

export const getDeltaToneStyles = (
  tone: DeltaTone,
): {
  color: PillColor;
  textClassName: string;
  icon: ComponentType<{ className?: string }>;
} => {
  switch (tone) {
    case "positive":
      return {
        color: Color.Green,
        textClassName: "text-green-700",
        icon: IconArrowUpRight,
      };
    case "negative":
      return {
        color: Color.Red,
        textClassName: "text-destructive",
        icon: IconArrowDownRight,
      };
    case "neutral":
    default:
      return {
        color: Color.Gray,
        textClassName: "text-gray-600",
        icon: IconArrowRight,
      };
  }
};

export const DeltaPill: FC<{ delta: string; tone: DeltaTone }> = ({
  delta,
  tone,
}) => {
  const style = getDeltaToneStyles(tone);
  const Icon = style.icon;

  return (
    <Pill
      color={style.color}
      size={Size.SMALL}
      label={
        <span
          className={twMerge(
            "inline-flex items-center gap-1",
            style.textClassName,
          )}
        >
          <Icon className="h-3.5 w-3.5" />
          {delta}
        </span>
      }
    />
  );
};
